import { Table, ActionIcon, Menu } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';

import { CustomTable, CustomTableMobile, useResponsive } from '@/shared';
import { EditShopCommissionForm } from './edit-sc-form';
import { useUpdateShopCommission } from '../hooks/use-update-shop-commission';
import { useDeleteShopCommission } from '../hooks/use-delete-shop-commission';

import type { TShopCommission } from '../store/types';

type Props = {
  items: TShopCommission[];
  isLoading?: boolean;
  itemsCount?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
};

export const ShopCommissionsTable = (props: Props) => {
  const { items } = props;
  const { isDesktop } = useResponsive();

  const {
    openEditModal,
    handleShopCommissionUpdate,
  } = useUpdateShopCommission();

  const { openDeleteModal } = useDeleteShopCommission();

  const getActions = (item: TShopCommission) => (
    <Menu position="bottom-end" shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="subtle">
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconEdit size={14} />}
          onClick={() => openEditModal(item, (
            <EditShopCommissionForm
              commission={item}
              onSubmit={handleShopCommissionUpdate}
              onClose={modals.closeAll}
            />
          ))}
        >
          Редактировать
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() => openDeleteModal(item)}
        >
          Удалить
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const titles = [
    'ID комиссии',
    'ID магазина',
    'Название магазина',
    'ID платёжного метода',
    'Название платёжного метода',
    'Процент пополнения',
    'Комиссия с клиента (%)',
  ];

  const rowsDesktop = items.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>{item.shopId}</Table.Td>
      <Table.Td>{item.shopName}</Table.Td>
      <Table.Td>{item.paymentMethodId}</Table.Td>
      <Table.Td>{item.paymentMethodName}</Table.Td>
      <Table.Td>{item.refillPercent}</Table.Td>
      <Table.Td>{item.additionalPercent}</Table.Td>
      <Table.Td width={50}>{getActions(item)}</Table.Td>
    </Table.Tr>
  ));

  const rowsMobile = items.map((item) => ({
    key: item.id,
    headerActions: getActions(item),
    items: [
      { name: 'Магазин', value: `(${item.shopId}) ${item.shopName}` },
      { name: 'Метод', value: `(${item.paymentMethodId}) ${item.paymentMethodName}` },
      { name: 'Процент пополнения', value: item.refillPercent },
      { name: 'Комиссия с клиента (%)', value: item.additionalPercent },
      { name: 'ID комиссии', value: item.id },
      { name: 'ID магазина', value: item.shopId },
      { name: 'Название магазина', value: item.shopName },
      { name: 'ID платёжного метода', value: item.paymentMethodId },
      { name: 'Название платёжного метода', value: item.paymentMethodName },
    ],
  }));

  return isDesktop
    ? (
      <CustomTable
        {...props}
        titles={titles}
        rows={rowsDesktop}
      />
    )
    : (
      <CustomTableMobile
        {...props}
        rows={rowsMobile}
        headerPointsCount={3}
      />
    );
};
